import { Component } from '@theme/component';

/**
 * "Lights on / off" switch. Writes `data-lights="on|off"` to the closest ancestor
 * matching `data-target` (defaults to `.product-media-container`), which owns the
 * lit/dimmed styles.
 *
 * @typedef {object} LightsToggleRefs
 * @property {HTMLButtonElement} switch - The role="switch" button
 *
 * @extends {Component<LightsToggleRefs>}
 */
class LightsToggle extends Component {
  requiredRefs = ['switch'];

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.#bind();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bind();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Native listener so the click stops here instead of reaching ancestor handlers
   * such as a gallery slide's zoom.
   */
  #bind() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.refs.switch.addEventListener('click', this.handleClick, { signal: this.#abortController.signal });
    this.#apply(this.refs.switch.getAttribute('aria-checked') === 'true');
  }

  /**
   * @param {MouseEvent} event
   */
  handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.toggle();
  };

  /**
   * Flips the lights state.
   * @param {boolean} [force] - Set a specific state instead of toggling
   */
  toggle(force) {
    const lit = force ?? this.refs.switch.getAttribute('aria-checked') !== 'true';
    this.#apply(lit);
  }

  /**
   * @param {boolean} lit
   */
  #apply(lit) {
    this.refs.switch.setAttribute('aria-checked', String(lit));
    const target = this.closest(this.dataset.target || '.product-media-container');
    if (target instanceof HTMLElement) target.dataset.lights = lit ? 'on' : 'off';
  }
}

if (!customElements.get('lights-toggle')) {
  customElements.define('lights-toggle', LightsToggle);
}
